import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Clock, Edit, Plus, AlertTriangle, CheckCircle, Thermometer } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'numeric', year: 'numeric' });

const fmtTime = (iso) =>
  new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

export default function ReviewTimeSheet() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [editForm, setEditForm] = useState({ clock_in: '', clock_out: '' });
  const [newOpen, setNewOpen] = useState(false);
  const [newForm, setNewForm] = useState({ clock_in: '', clock_out: '' });
  const [sickOpen, setSickOpen] = useState(false);
  const [sickForm, setSickForm] = useState({ start_date: '', end_date: '' });

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/time/review/${token}`);
      setData(res.data);
      setError(null);
    } catch (err) {
      const status = err.response?.status;
      if (status === 410) setError('This link has expired.');
      else if (status === 404) setError('Link not found.');
      else setError('Something went wrong.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEdit = (entry) => {
    setEditEntry(entry);
    setEditForm({
      clock_in: entry.clock_in ? new Date(entry.clock_in).toISOString().slice(0, 16) : '',
      clock_out: entry.clock_out ? new Date(entry.clock_out).toISOString().slice(0, 16) : '',
    });
    setEditOpen(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/time/review/${token}/entries/${editEntry.id}`, {
        clock_in: new Date(editForm.clock_in).toISOString(),
        clock_out: editForm.clock_out ? new Date(editForm.clock_out).toISOString() : null,
      });
      toast.success('Entry updated!');
      setEditOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const handleCreateEntry = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/time/review/${token}/entries`, {
        clock_in: new Date(newForm.clock_in).toISOString(),
        clock_out: newForm.clock_out ? new Date(newForm.clock_out).toISOString() : null,
      });
      toast.success('New entry created!');
      setNewOpen(false);
      setNewForm({ clock_in: '', clock_out: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create entry');
    }
  };

  const handleCreateSickLeave = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/time/review/${token}/sick-leave`, {
        start_date: sickForm.start_date,
        end_date: sickForm.end_date,
      });
      toast.success('Sick leave registered!');
      setSickOpen(false);
      setSickForm({ start_date: '', end_date: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to register sick leave');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <p className="text-lg text-gray-500">Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="py-12 text-center space-y-4">
            <AlertTriangle className="h-12 w-12 text-red-500 mx-auto" />
            <p className="text-lg font-semibold text-red-600">{error}</p>
            <p className="text-sm text-gray-500">Contact your manager for a new link.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const pending = data.entries.filter((e) => e.status !== 'approved');
  const approved = data.entries.filter((e) => e.status === 'approved');
  const sickLeaves = data.sick_leaves || [];
  const expiresDate = new Date(data.expires_at);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="review-page">
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-indigo-100 mb-2">
            <Clock className="h-7 w-7 text-indigo-600" />
          </div>
          <h1 className="text-2xl font-bold" data-testid="review-title">
            Timesheet &ndash; {data.user_name}
          </h1>
          <p className="text-sm text-gray-500">
            {data.from_date && data.to_date ? (
              <>Period: {data.from_date} to {data.to_date} &middot; </>
            ) : null}
            Link valid until {expiresDate.toLocaleDateString('en-GB')}
          </p>
        </div>

        {/* Action buttons */}
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => setSickOpen(true)}
            className="text-orange-600 border-orange-300 hover:bg-orange-50"
            data-testid="review-sick-leave-btn"
          >
            <Thermometer className="h-4 w-4 mr-2" />
            Report sick leave
          </Button>
          <Button onClick={() => setNewOpen(true)} data-testid="review-new-entry-btn">
            <Plus className="h-4 w-4 mr-2" />
            New time entry
          </Button>
        </div>

        {/* Sick leaves - expanded per day */}
        {sickLeaves.length > 0 && (() => {
          const sickRows = [];
          sickLeaves.forEach((leave) => {
            const start = new Date(leave.start_date + 'T00:00:00');
            const end = new Date(leave.end_date + 'T00:00:00');
            const current = new Date(start);
            while (current <= end) {
              sickRows.push({ date: new Date(current), id: leave.id + '_' + current.toISOString() });
              current.setDate(current.getDate() + 1);
            }
          });
          return (
            <Card className="bg-orange-50 border-orange-200" data-testid="review-sick-section">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Thermometer className="h-5 w-5 text-orange-600" />
                  Sick days ({sickRows.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="divide-y divide-orange-200">
                  {sickRows.map((row) => (
                    <div key={row.id} className="flex items-center justify-between py-2" data-testid={`review-sick-day`}>
                      <span className="text-sm">{fmtDate(row.date.toISOString())}</span>
                      <span className="text-sm font-semibold text-orange-700">Sick &ndash; 7.5 hrs</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* Pending entries */}
        <div className="space-y-3">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
            Pending entries ({pending.length})
          </h2>
          {pending.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-gray-500">
                No pending entries
              </CardContent>
            </Card>
          ) : (
            pending.map((entry) => (
              <Card key={entry.id} className="border-yellow-200" data-testid={`review-entry-${entry.id}`}>
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="font-medium">{fmtDate(entry.clock_in)}</p>
                      <div className="flex gap-6 text-sm text-gray-600">
                        <span>In: {fmtTime(entry.clock_in)}</span>
                        <span>Out: {entry.clock_out ? fmtTime(entry.clock_out) : '—'}</span>
                        <span>{entry.total_hours ? `${entry.total_hours} hrs` : ''}</span>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(entry)}
                      data-testid={`review-edit-${entry.id}`}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Approved entries (read-only) */}
        {approved.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              Approved entries ({approved.length})
            </h2>
            {approved.map((entry) => (
              <Card key={entry.id} className="border-green-200 opacity-70" data-testid={`review-approved-${entry.id}`}>
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="font-medium">{fmtDate(entry.clock_in)}</p>
                      <div className="flex gap-6 text-sm text-gray-600">
                        <span>In: {fmtTime(entry.clock_in)}</span>
                        <span>Out: {entry.clock_out ? fmtTime(entry.clock_out) : '—'}</span>
                        <span>{entry.total_hours ? `${entry.total_hours} hrs` : ''}</span>
                      </div>
                    </div>
                    <span className="text-xs font-medium bg-green-100 text-green-700 px-2 py-1 rounded-full">
                      Approved
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent aria-describedby="edit-review-desc">
            <DialogHeader>
              <DialogTitle>Edit time entry</DialogTitle>
            </DialogHeader>
            <p id="edit-review-desc" className="sr-only">Edit clock in and out times</p>
            <form onSubmit={handleSaveEdit} className="space-y-4">
              <div className="space-y-2">
                <Label>Clock in</Label>
                <Input
                  type="datetime-local"
                  value={editForm.clock_in}
                  onChange={(e) => setEditForm({ ...editForm, clock_in: e.target.value })}
                  required
                  data-testid="review-edit-clock-in"
                />
              </div>
              <div className="space-y-2">
                <Label>Clock out</Label>
                <Input
                  type="datetime-local"
                  value={editForm.clock_out}
                  onChange={(e) => setEditForm({ ...editForm, clock_out: e.target.value })}
                  data-testid="review-edit-clock-out"
                />
              </div>
              <Button type="submit" className="w-full" data-testid="review-save-edit">
                Save
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* New Entry Dialog */}
        <Dialog open={newOpen} onOpenChange={setNewOpen}>
          <DialogContent aria-describedby="new-review-desc">
            <DialogHeader>
              <DialogTitle>New time entry</DialogTitle>
            </DialogHeader>
            <p id="new-review-desc" className="sr-only">Add a new time entry</p>
            <form onSubmit={handleCreateEntry} className="space-y-4">
              <div className="space-y-2">
                <Label>Clock in</Label>
                <Input
                  type="datetime-local"
                  value={newForm.clock_in}
                  onChange={(e) => setNewForm({ ...newForm, clock_in: e.target.value })}
                  required
                  data-testid="review-new-clock-in"
                />
              </div>
              <div className="space-y-2">
                <Label>Clock out</Label>
                <Input
                  type="datetime-local"
                  value={newForm.clock_out}
                  onChange={(e) => setNewForm({ ...newForm, clock_out: e.target.value })}
                  data-testid="review-new-clock-out"
                />
              </div>
              <Button type="submit" className="w-full" data-testid="review-save-new">
                Save
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Sick Leave Dialog */}
        <Dialog open={sickOpen} onOpenChange={setSickOpen}>
          <DialogContent aria-describedby="sick-review-desc">
            <DialogHeader>
              <DialogTitle>Report sick leave</DialogTitle>
            </DialogHeader>
            <p id="sick-review-desc" className="sr-only">Register sick days</p>
            <form onSubmit={handleCreateSickLeave} className="space-y-4">
              <div className="space-y-2">
                <Label>From</Label>
                <Input
                  type="date"
                  value={sickForm.start_date}
                  onChange={(e) => setSickForm({ ...sickForm, start_date: e.target.value })}
                  required
                  data-testid="review-sick-start"
                />
              </div>
              <div className="space-y-2">
                <Label>To</Label>
                <Input
                  type="date"
                  value={sickForm.end_date}
                  onChange={(e) => setSickForm({ ...sickForm, end_date: e.target.value })}
                  required
                  data-testid="review-sick-end"
                />
              </div>
              <Button type="submit" className="w-full" data-testid="review-save-sick">
                Submit
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
